import React from 'react';
import type {
  Control,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseFormStateReturn,
} from 'react-hook-form';
import { cn } from '../lib/utils';
import { Tip } from './Tip';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from './ui/form';

/**
 * Simplified version of the `<FormField>` component.
 */
export const SimpleFormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  className,
  render,
}: {
  className?: string;
  control?: Control<TFieldValues, any, TFieldValues>;
  name: TName;
  label: React.ReactNode;
  description?: React.ReactNode;
  render: ({
    field,
    fieldState,
    formState,
  }: {
    field: ControllerRenderProps<TFieldValues, TName>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<TFieldValues>;
  }) => React.ReactElement;
}) => {
  return (
    <FormField
      control={control}
      name={name}
      render={(props) => (
        <FormItem className={cn('text-text-primary', className)}>
          <FormLabel className="text-text-primary">
            {label}
            {description && <Tip>{description}</Tip>}
          </FormLabel>
          <FormControl>{render(props)}</FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
